import EqualizerIcon from '@mui/icons-material/Equalizer';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
    Box,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    useMediaQuery,
} from '@mui/material';
import React from 'react';
import { useGetExternalGuestAttendeeStatsQuery } from '../../../../../app/Slices/SevenDayApiSlice';
import { openDashboardReportExportMenu } from '../../dashboardReportTableConfig';
import { StatsBreakdown, StatsRange } from '../../../../enums/reports';
import { CustomButton } from '../../../../../layout/styledcomponents/CustomButton';
import {
    dashboardReportBreakdownOptions,
    dashboardReportRangeOptions,
} from '../../dashboardPeriod';
import { useDashboardReportFilters } from '../../useDashboardReportFilters';
import ExternalGuestAttendeesReportGraph from './ExternalGuestAttendeesReportGraph';
import ExternalGuestAttendeesReportList from './ExternalGuestAttendeesReportList';
import ExternalGuestAttendeesItemsList from './ExternalGuestAttendeesItemsList';
import ReportTableTabs, { ReportTableTab } from '../../ReportTableTabs';

export default function ExternalGuestAttendeesComponentPage() {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const [graphView, setGraphView] = React.useState(true);
    const [tableTab, setTableTab] = React.useState<ReportTableTab>('summary');
    const { range, breakdown, setRange, setBreakdown } =
        useDashboardReportFilters();

    const { data, isLoading } = useGetExternalGuestAttendeeStatsQuery({
        range,
        breakdown,
        includeAttendees: true,
    });

    return (
        <Grid sx={{ mt: 1 }}>
            <Grid
                sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between',
                    gap: 1.5,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: 1.5,
                        width: isMobile ? '100%' : 'auto',
                    }}
                >
                    <FormControl size='small' sx={{ minWidth: 170 }}>
                        <InputLabel id='external-guest-attendee-range-label'>
                            Range
                        </InputLabel>
                        <Select
                            labelId='external-guest-attendee-range-label'
                            value={range}
                            label='Range'
                            onChange={(event) =>
                                setRange(event.target.value as StatsRange)
                            }
                        >
                            {dashboardReportRangeOptions.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size='small' sx={{ minWidth: 170 }}>
                        <InputLabel id='external-guest-attendee-breakdown-label'>
                            Breakdown
                        </InputLabel>
                        <Select
                            labelId='external-guest-attendee-breakdown-label'
                            value={breakdown}
                            label='Breakdown'
                            onChange={(event) =>
                                setBreakdown(
                                    event.target.value as StatsBreakdown,
                                )
                            }
                        >
                            {dashboardReportBreakdownOptions.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {graphView ? (
                        <CustomButton
                            disableRipple
                            variant='outlined'
                            color='primary'
                            onClick={() => setGraphView(false)}
                        >
                            <FormatListBulletedIcon fontSize='small' />
                        </CustomButton>
                    ) : (
                        <>
                            <CustomButton
                                disableRipple
                                variant='outlined'
                                color='primary'
                                onClick={() => setGraphView(true)}
                            >
                                <EqualizerIcon fontSize='small' />
                            </CustomButton>
                            {tableTab === 'items' ? (
                                <CustomButton
                                    disableRipple
                                    variant='contained'
                                    color='neutral'
                                    onClick={openDashboardReportExportMenu}
                                >
                                    <FileDownloadIcon fontSize='small' />
                                </CustomButton>
                            ) : null}
                        </>
                    )}
                </Box>
            </Grid>

            <Typography sx={{ mt: 2 }} variant='h5'>
                {isLoading
                    ? 'Loading...'
                    : `Total External Guest Attendees: ${data?.totalExternalGuestAttendees ?? 0}`}
            </Typography>

            {graphView ? (
                <ExternalGuestAttendeesReportGraph
                    points={data?.points ?? []}
                />
            ) : (
                <ReportTableTabs
                    summaryLabel='By Period'
                    itemsLabel='Individual Attendees'
                    onTabChange={setTableTab}
                    summaryContent={
                        <ExternalGuestAttendeesReportList
                            points={data?.points ?? []}
                        />
                    }
                    itemsContent={
                        <ExternalGuestAttendeesItemsList
                            attendees={data?.attendees ?? []}
                        />
                    }
                />
            )}
        </Grid>
    );
}
